// ============================================================
// ClientExportsTab.js — Platform-07: the termination-export surface
// inside <ClientDetailPage>.
//
// Request an export → the queue worker builds the package → download via a
// 60-minute SAS → record the client's acknowledgement of receipt (the A2Z
// IT record AC-4 §6 requires; note mandatory, stamped once).
//
// The completeness flag is surfaced LOUDLY: an incomplete archive (a table
// read failed, a PDF report failed, a stored blob was missing) must never
// be handed to a terminating client as if it were the full record.
// ============================================================

import React, { useCallback, useEffect, useRef, useState } from 'react';
import DataTable from '../components/DataTable';
import Badge from '../components/Badge';
import ConfirmDialog from '../components/ConfirmDialog';
import Modal from '../components/Modal';
import { toast } from '../components/Toaster';
import {
  listClientExports, requestClientExport, getClientExportDownload,
  acknowledgeClientExport, formatSize,
} from '../api/clientExports';
import { translateError } from '../utils/errorMessages';
import { formatDateTime } from '../utils/format';
import ClientPurgePanel from '../components/ClientPurgePanel';
import './ClientExportsTab.css';

const STATUS_BADGE = {
  queued: { variant: 'info', label: 'Queued' },
  running: { variant: 'info', label: 'Running' },
  complete: { variant: 'success', label: 'Complete' },
  failed: { variant: 'danger', label: 'Failed' },
};

// Queued/running rows resolve without the operator refreshing by hand.
const POLL_MS = 15000;

export default function ClientExportsTab({ clientId, clientName }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [ackTarget, setAckTarget] = useState(null);
  const [ackNote, setAckNote] = useState('');
  const pollRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoadError(null);
    try {
      const res = await listClientExports(clientId);
      setItems(res.items || []);
    } catch (err) {
      setLoadError(translateError(err) || 'Failed to load exports');
    } finally {
      setLoading(false);
    }
  }, [clientId]);
  useEffect(() => { setLoading(true); fetchAll(); }, [fetchAll]);

  // Poll while any export is in flight.
  useEffect(() => {
    const inFlight = items.some((i) => i.status === 'queued' || i.status === 'running');
    if (inFlight && !pollRef.current) {
      pollRef.current = setInterval(fetchAll, POLL_MS);
    }
    if (!inFlight && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [items, fetchAll]);

  async function handleConfirmRequest() {
    setBusy(true);
    try {
      await requestClientExport(clientId);
      toast.success('Export queued. The package builds in the background.');
      setRequestOpen(false);
      await fetchAll();
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to request the export');
    } finally { setBusy(false); }
  }

  async function handleDownload(row) {
    setBusy(true);
    try {
      const res = await getClientExportDownload(clientId, row.exportId);
      window.open(res.url, '_blank', 'noopener');
      toast.success(`Download link opened (valid ${res.expiresInMinutes} minutes).`);
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to mint the download link');
    } finally { setBusy(false); }
  }

  async function handleConfirmAcknowledge() {
    if (!ackTarget) return;
    setBusy(true);
    try {
      await acknowledgeClientExport(clientId, ackTarget.exportId, ackNote.trim());
      toast.success('Acknowledgement recorded.');
      setAckTarget(null);
      setAckNote('');
      await fetchAll();
    } catch (err) {
      toast.error(err?.detail || err?.message || 'Failed to record the acknowledgement');
    } finally { setBusy(false); }
  }

  const columns = [
    {
      key: 'requestedAt',
      label: 'Requested',
      render: (row) => (
        <div>
          <div>{formatDateTime(row.requestedAt)}</div>
          <div className="client-exports__sub">{row.requestedBy}</div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      render: (row) => {
        const cfg = STATUS_BADGE[row.status] || { variant: 'neutral', label: row.status };
        return (
          <div>
            <Badge variant={cfg.variant}>{cfg.label}</Badge>
            {row.status === 'complete' && row.archiveComplete === false && (
              <div><Badge variant="warning">Incomplete — see manifest</Badge></div>
            )}
            {row.status === 'failed' && row.errorDetail && (
              <div className="client-exports__sub" title={row.errorDetail}>{row.errorDetail.slice(0, 80)}</div>
            )}
          </div>
        );
      },
    },
    {
      key: 'contents',
      label: 'Contents',
      render: (row) => (row.status === 'complete'
        ? (
          <div className="client-exports__sub">
            <div>{row.tablesExported} tables · {row.totalRows} rows{row.tablesFailed ? ` · ${row.tablesFailed} FAILED` : ''}</div>
            <div>{row.pdfReportsGenerated} PDF reports{row.pdfReportsFailed ? ` · ${row.pdfReportsFailed} FAILED` : ''} · {row.blobsCopied} files</div>
          </div>
        )
        : '—'),
    },
    {
      key: 'sizeBytes',
      label: 'Size',
      render: (row) => formatSize(row.sizeBytes),
    },
    {
      key: 'acknowledged',
      label: 'Receipt',
      render: (row) => (row.acknowledgedAt
        ? (
          <div>
            <Badge variant="success">Acknowledged</Badge>
            <div className="client-exports__sub">{formatDateTime(row.acknowledgedAt)} · {row.acknowledgedBy}</div>
          </div>
        )
        : <span className="client-exports__sub">Not recorded</span>),
    },
    {
      key: 'actions',
      label: '',
      render: (row) => (
        <div className="client-exports__actions">
          {row.status === 'complete' && (
            <button type="button" className="client-exports__btn" onClick={() => handleDownload(row)} disabled={busy}>
              Download
            </button>
          )}
          {row.status === 'complete' && !row.acknowledgedAt && (
            <button type="button" className="client-exports__btn" onClick={() => { setAckTarget(row); setAckNote(''); }} disabled={busy}>
              Record receipt
            </button>
          )}
        </div>
      ),
    },
  ];

  const inFlight = items.some((i) => i.status === 'queued' || i.status === 'running');

  return (
    <div className="client-exports">
      <div className="client-exports__toolbar">
        <button
          type="button"
          className="client-exports__btn client-exports__btn--primary"
          onClick={() => setRequestOpen(true)}
          disabled={busy || inFlight}
          title={inFlight ? 'An export is already queued or running for this client.' : undefined}
        >
          Request export
        </button>
        <div className="client-exports__hint">
          The full termination package (AC-4 §6): every client-data table as JSON + CSV, the
          human-readable PDF reports, every attachment with original filenames, and a manifest
          stating what is included, what was withheld and why, and whether the package is complete.
        </div>
      </div>

      <DataTable
        columns={columns}
        data={items}
        rowKey={(row) => row.exportId}
        totalCount={items.length}
        page={1}
        pageSize={items.length || 1}
        onPageChange={() => {}}
        loading={loading}
        error={loadError}
        onRetry={fetchAll}
        emptyMessage="No exports have been produced for this Client."
      />

      {/* Platform-08: the purge surface lives with the exports because the
          export IS its precondition — the checklist reads the same ledger. */}
      <ClientPurgePanel clientId={clientId} clientName={clientName} />

      <ConfirmDialog
        isOpen={requestOpen}
        onClose={() => setRequestOpen(false)}
        onConfirm={handleConfirmRequest}
        title="Request termination export"
        confirmLabel={busy ? 'Queuing…' : 'Queue the export'}
        tone="primary"
        isSubmitting={busy}
        message={(
          <>
            <p>
              Build the complete data-export package for <strong>{clientName || `client ${clientId}`}</strong>?
            </p>
            <p>
              The package is built in the background and appears here when ready. Nothing is deleted
              or changed — an export is a read.
            </p>
          </>
        )}
      />

      <Modal
        isOpen={Boolean(ackTarget)}
        onClose={() => { setAckTarget(null); setAckNote(''); }}
        title="Record client receipt"
      >
        <p>
          Record that the client confirmed receipt of this export. This is kept as the A2Z IT
          record and cannot be amended once saved.
        </p>
        <label className="client-exports__field">
          <span>Who at the client confirmed receipt, and how?</span>
          <textarea
            value={ackNote}
            onChange={(e) => setAckNote(e.target.value)}
            rows={4}
            maxLength={2000}
            placeholder="e.g. Jane Smith (Registered Manager) confirmed receipt by email on 13/08/2026; signed acknowledgement form returned and filed."
          />
        </label>
        <div className="client-exports__modal-actions">
          <button
            type="button"
            className="client-exports__btn"
            onClick={() => { setAckTarget(null); setAckNote(''); }}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="client-exports__btn client-exports__btn--primary"
            onClick={handleConfirmAcknowledge}
            disabled={busy || !ackNote.trim()}
          >
            {busy ? 'Saving…' : 'Record receipt'}
          </button>
        </div>
      </Modal>
    </div>
  );
}
